import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { palletAPI, dropdownAPI } from '../services/api';

const PalletSheetForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, logout } = useAuth();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Get today's date in local timezone (AEST)
  const getTodayLocal = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    customerName: '',
    customerAddress: '',
    sheetDate: getTodayLocal(),
  });
  const [entries, setEntries] = useState([]);
  const [dropdowns, setDropdowns] = useState({
    grower: [],
    produce_type: [],
    unit: [],
    class: [],
    size: [],
    colour: [],
  });

  useEffect(() => {
    fetchDropdowns();
    if (isEditMode) {
      fetchSheet();
    }
  }, [id]);

  const fetchDropdowns = async () => {
    try {
      const data = await dropdownAPI.getAll();
      setDropdowns(data.lists || {});
    } catch (err) {
      console.error('Failed to fetch dropdowns:', err);
    }
  };

  const fetchSheet = async () => {
    try {
      const data = await palletAPI.getSheet(id);
      setFormData({
        customerName: data.sheet.customer_name || '',
        customerAddress: data.sheet.customer_address || '',
        sheetDate: data.sheet.sheet_date?.split('T')[0] || '',
      });

      // Normalize entries to camelCase for consistent editing
      const normalizedEntries = (data.entries || []).map(entry => ({
        palletNumber: entry.pallet_number || '',
        grower: entry.grower || '',
        produceType: entry.produce_type || '',
        unit: entry.unit || '',
        classGrade: entry.class_grade || '',
        size: entry.size || '',
        colour: entry.colour || '',
        qty: entry.qty || 0,
      }));

      setEntries(normalizedEntries);
    } catch (err) {
      console.error('Failed to fetch sheet:', err);
      setError('Failed to load pallet sheet');
    }
  };

  const addEntry = () => {
    // Copy data from last entry if exists
    const lastEntry = entries.length > 0 ? entries[entries.length - 1] : null;

    const newEntry = lastEntry ? {
      palletNumber: lastEntry.palletNumber || '',
      grower: lastEntry.grower || '',
      produceType: lastEntry.produceType || '',
      unit: lastEntry.unit || '',
      classGrade: lastEntry.classGrade || '',
      size: lastEntry.size || '',
      colour: lastEntry.colour || '',
      qty: '',
    } : {
      palletNumber: '',
      grower: '',
      produceType: '',
      unit: '',
      classGrade: '',
      size: '',
      colour: '',
      qty: '',
    };

    setEntries([...entries, newEntry]);
  };

  // Check if an entry is a duplicate (all fields match another row)
  const isDuplicateEntry = (index) => {
    const entry = entries[index];

    // Find if there's another entry with ALL the same values (excluding qty)
    return entries.some((other, otherIndex) => {
      if (otherIndex === index) return false; // Don't compare with itself

      return (
        other.palletNumber === entry.palletNumber &&
        other.grower === entry.grower &&
        other.produceType === entry.produceType &&
        other.unit === entry.unit &&
        other.classGrade === entry.classGrade &&
        other.size === entry.size &&
        other.colour === entry.colour &&
        // Only flag as duplicate if key fields have values
        entry.palletNumber && entry.grower && entry.produceType
      );
    });
  };

  const removeEntry = (index) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const updateEntry = (index, field, value) => {
    const newEntries = [...entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    setEntries(newEntries);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        customerName: formData.customerName,
        customerAddress: formData.customerAddress,
        sheetDate: formData.sheetDate,
        entries: entries.map(entry => ({
          palletNumber: entry.palletNumber,
          grower: entry.grower,
          produceType: entry.produceType,
          unit: entry.unit,
          classGrade: entry.classGrade,
          size: entry.size,
          colour: entry.colour,
          qty: parseInt(entry.qty) || 0,
        })),
      };

      if (isEditMode) {
        await palletAPI.updateSheet(id, payload);
        setSuccess('Pallet sheet updated successfully! You can now email or print the sheet.');
      } else {
        const response = await palletAPI.createSheet(payload);
        setSuccess('Pallet sheet created successfully!');
        // Navigate to edit mode with the new sheet ID so user can email/print
        if (response.sheet?.id) {
          navigate(`/pallets/${response.sheet.id}/edit`, { replace: true });
        }
      }

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.error || 'Failed to save pallet sheet');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <h1 style={{ margin: 0, fontSize: '24px', color: '#10b981' }}>KFRESH</h1>
            <nav style={{ display: 'flex', gap: '20px' }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '15px',
                  color: '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Dashboard
              </button>
              <button
                onClick={() => navigate('/pallets')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '15px',
                  color: '#10b981',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Pallet Sheets
              </button>
              <button
                onClick={() => navigate('/reports')}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '15px',
                  color: '#374151',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Reports
              </button>
              {user?.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '15px',
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Admin
                </button>
              )}
            </nav>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '600', fontSize: '14px' }}>
                {user?.firstName} {user?.lastName}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </div>
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px 40px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <h2 style={{ margin: 0 }}>{isEditMode ? 'Edit' : 'Create'} Pallet Sheet</h2>
          <button
            onClick={() => navigate('/pallets')}
            style={{
              padding: '8px 16px',
              backgroundColor: 'white',
              color: '#374151',
              border: '2px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            ← Back to List
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            color: '#991b1b',
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            backgroundColor: '#d1fae5',
            border: '1px solid #10b981',
            color: '#065f46',
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '12px' }}>
              ✓ {success}
            </div>
            {isEditMode && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => navigate(`/pallets/${id}`)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                >
                  View Sheet
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/pallets')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'white',
                    color: '#065f46',
                    border: '1px solid #10b981',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f0fdf4';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'white';
                  }}
                >
                  Back to List
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Sheet Details */}
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Sheet Details</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>
                  Customer Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.customerName}
                  onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>
                  Sheet Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.sheetDate}
                  onChange={(e) => setFormData({ ...formData, sheetDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '6px', color: '#374151' }}>
                Customer Address
              </label>
              <textarea
                value={formData.customerAddress}
                onChange={(e) => setFormData({ ...formData, customerAddress: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>

          {/* Pallet Entries */}
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 8px 0' }}>Pallet Entries</h3>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 20px 0' }}>
              <strong>Tip:</strong> Click "Add Entry" to copy values from the last row. Duplicate rows are highlighted in yellow.
            </p>

            {entries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', border: '2px dashed #e5e7eb', borderRadius: '8px' }}>
                <p style={{ color: '#6b7280', margin: '0 0 16px 0' }}>
                  No entries yet. Click "Add Entry" to get started.
                </p>
                <button
                  type="button"
                  onClick={addEntry}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
                >
                  + Add Entry
                </button>
              </div>
            ) : (
              <>
              <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', width: '40px' }}>#</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Pallet #</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Grower</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Produce Type</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Unit</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Class</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Size</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Colour</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Qty</th>
                      <th style={{ padding: '10px 8px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151', width: '80px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, index) => {
                      const isDuplicate = isDuplicateEntry(index);
                      return (
                      <tr key={index} style={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: isDuplicate ? '#fef3c7' : (index % 2 === 0 ? 'white' : '#fafafa'),
                        borderLeft: isDuplicate ? '4px solid #f59e0b' : 'none'
                      }}>
                        <td style={{ padding: '6px 8px', fontSize: '12px', color: '#6b7280' }}>{index + 1}</td>
                        <td style={{ padding: '6px 8px' }}>
                          <input
                            type="text"
                            value={entry.palletNumber || ''}
                            onChange={(e) => updateEntry(index, 'palletNumber', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '13px',
                              boxSizing: 'border-box'
                            }}
                          />
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <select
                            value={entry.grower || ''}
                            onChange={(e) => updateEntry(index, 'grower', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '13px',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">-</option>
                            {dropdowns.grower && dropdowns.grower.map((grower, i) => (
                              <option key={i} value={grower}>{grower}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <select
                            value={entry.produceType || ''}
                            onChange={(e) => updateEntry(index, 'produceType', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '13px',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">-</option>
                            {dropdowns.produce_type && dropdowns.produce_type.map((type, i) => (
                              <option key={i} value={type}>{type}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <select
                            value={entry.unit || ''}
                            onChange={(e) => updateEntry(index, 'unit', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '13px',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">-</option>
                            {dropdowns.unit && dropdowns.unit.map((unit, i) => (
                              <option key={i} value={unit}>{unit}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <select
                            value={entry.classGrade || ''}
                            onChange={(e) => updateEntry(index, 'classGrade', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '13px',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">-</option>
                            {dropdowns.class && dropdowns.class.map((cls, i) => (
                              <option key={i} value={cls}>{cls}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <select
                            value={entry.size || ''}
                            onChange={(e) => updateEntry(index, 'size', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '13px',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">-</option>
                            {dropdowns.size && dropdowns.size.map((size, i) => (
                              <option key={i} value={size}>{size}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <select
                            value={entry.colour || ''}
                            onChange={(e) => updateEntry(index, 'colour', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '13px',
                              boxSizing: 'border-box'
                            }}
                          >
                            <option value="">-</option>
                            {dropdowns.colour && dropdowns.colour.map((colour, i) => (
                              <option key={i} value={colour}>{colour}</option>
                            ))}
                          </select>
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <input
                            type="number"
                            value={entry.qty || ''}
                            onChange={(e) => updateEntry(index, 'qty', e.target.value)}
                            onFocus={(e) => e.target.select()}
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '13px',
                              boxSizing: 'border-box',
                              textAlign: 'right'
                            }}
                          />
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => removeEntry(index)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '11px',
                              cursor: 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <button
                type="button"
                onClick={addEntry}
                style={{
                  marginTop: '16px',
                  padding: '10px 20px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  width: '100%'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#10b981'}
              >
                + Add Entry
              </button>
              </>
            )}
          </div>

          {/* Submit Buttons */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => navigate('/pallets')}
              style={{
                padding: '12px 24px',
                backgroundColor: 'white',
                color: '#374151',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '12px 24px',
                backgroundColor: loading ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Saving...' : isEditMode ? 'Update Sheet' : 'Create Sheet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PalletSheetForm;
